import { t } from "@lingui/macro";
import { format, parse } from "date-fns";
import React from "react";
import type { PreloadedQuery } from "react-relay";

import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { PlaceIcon } from "../../../../../../components/icons_v2/general";
import type { EventCardPairingsQuery as EventCardPairingsQueryType } from "./__generated__/EventCardPairingsQuery.graphql";
import { EventCardPairingBooking } from "./components/EventCardPairingBooking/EventCardPairingBooking";
import { useGetEventCardPairings } from "./hooks/useGetEventCardPairings";

export type EventCardPairingsInnerProps = {
  queryRef: PreloadedQuery<EventCardPairingsQueryType>;
};

export const EventCardPairingsInner = ({
  queryRef,
}: EventCardPairingsInnerProps) => {
  const allBookings = useGetEventCardPairings(queryRef);

  return (
    <Box pt={4}>
      <VStack gap={2} width="100%">
        {allBookings.map((booking, bookingIndex) => {
          const rounds = booking.rounds ?? [];
          const isYourBooking = booking.rounds.some((r) => r.isYou);

          return (
            <VStack
              key={`${booking.courseName ?? ""}-${
                booking.time ?? ""
              }-${bookingIndex}`}
              gap={3}
              pt={2}
              width="100%"
              borderWidth={1.5}
              borderColor={
                isYourBooking ? "outline-primary" : "outline-default"
              }
              borderRadius="4"
              padding={4}
              backgroundColor="surface-secondary"
            >
              <HStack
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <VStack>
                  {booking.isMultiCourse ? (
                    <HStack gap={1}>
                      <Text variant="mainTiny" color="text-secondary">
                        {booking.courseName}
                      </Text>
                      <PlaceIcon color="icon-disabled" />
                    </HStack>
                  ) : null}
                  <Text variant="mainBaseSemiBold">
                    {booking.time
                      ? format(
                          parse(booking.time, "HH:mm:ss", new Date()),
                          "h:mm aaa"
                        )
                      : ""}
                  </Text>
                </VStack>
                {isYourBooking ? (
                  <Text
                    variant="mainSmall"
                    fontWeight="600"
                  >{t`Your Booking`}</Text>
                ) : null}
              </HStack>
              <HStack gap={2} flexWrap="wrap" alignItems="center">
                {rounds.map((round, roundIndex: number) => {
                  return (
                    <EventCardPairingBooking
                      key={roundIndex}
                      playerName={round.name}
                      courseLayout={round.courseLayout}
                      transportationType={round.transportationType}
                      isYou={round.isYou}
                      isYourBooking={isYourBooking}
                    />
                  );
                })}
              </HStack>
            </VStack>
          );
        })}
      </VStack>
    </Box>
  );
};
